import { ALERT_STATUS } from '../consts'
import { catchWithSentryCapture } from '../utils'
import { noRelevanceSchema, patentNaturalRelevanceSchema } from './alert'
import { datetimeSchema } from './datetime'
import { officeActionEventSchema } from './office-actions'
import { z } from 'zod'

const baseSchema = z.object({
  id: z.string().min(1),
  monitored_document: z
    .object({ id: z.string(), document_number: z.string(), title: z.string() })
    .nullable()
    .optional(),
  alert_document_number: z.string(),
  alert_document_title: z.string(),
  alert_document_publication_date: z.string().default(''),
  alert_document_current_assignees: z
    .array(z.string())
    .optional()
    .default([])
    .transform((currentAssignees) =>
      currentAssignees.length > 0 ? currentAssignees.join(', ') : '-'
    ),
  status: z.enum(ALERT_STATUS),
})

const apiNaturalLanguageSchema = baseSchema
  .extend({
    relevance: patentNaturalRelevanceSchema.nullable(),
    relevance_analysis: z.string().nullable().optional().default(null),
    detailed_relevance_analysis: z.string().nullable().optional().default(null),
    type: z.literal('similar_patent'),
  })
  .transform((alert) => ({
    ...alert,
    relevanceAnalysis: alert.relevance,
    detailedRelevanceAnalysis: alert.detailed_relevance_analysis,
  }))

const apiPatentSchema = baseSchema
  .extend({
    relevance: patentNaturalRelevanceSchema.nullable(),
    relevance_analysis: z.string().nullable().optional().default(null),
    detailed_relevance_analysis: z.string().nullable().optional().default(null),

    type: z
      .literal('similar_patent')
      // This isn't a promise chain.
      // eslint-disable-next-line unicorn/prefer-top-level-await
      .catch(
        catchWithSentryCapture('similar_patent' as const, 'apiPatentSchema')
      ),
  })
  .transform((alert) => ({
    ...alert,
    relevanceAnalysis: alert.relevance,
    detailedRelevanceAnalysis: alert.detailed_relevance_analysis,
  }))

const apiKeywordFilterSchema = baseSchema.extend({
  relevance: noRelevanceSchema,
})

const apiOfficeActionSchema = baseSchema
  .extend({
    relevance: noRelevanceSchema,
    office_action_event_code: officeActionEventSchema.optional().nullable(),
    office_action_event_date: datetimeSchema.optional().nullable(),
  })
  .transform((alert) => ({
    ...alert,
    officeActionEventCode: alert.office_action_event_code,
    officeActionEventDate: alert.office_action_event_date,
  }))

const generateAlertSchema = (
  schema:
    | typeof apiKeywordFilterSchema
    | typeof apiNaturalLanguageSchema
    | typeof apiPatentSchema
    | typeof apiOfficeActionSchema
) =>
  schema.transform((alert) => {
    const {
      monitored_document,
      alert_document_number,
      alert_document_title,
      alert_document_publication_date,
      alert_document_current_assignees,
      ...alertFields
    } = alert

    return {
      ...alertFields,
      monitoredDocument: monitored_document,
      alertDocumentNumber: alert_document_number,
      alertDocumentTitle: alert_document_title,
      alertDocumentPublicationDate: alert_document_publication_date,
      alertDocumentCurrentAssignees: alert_document_current_assignees,
    }
  })

export const naturalLanguageAlertSchema = generateAlertSchema(
  apiNaturalLanguageSchema
)
export const patentAlertSchema = generateAlertSchema(apiPatentSchema)

export const officeActionAlertSchema = generateAlertSchema(
  apiOfficeActionSchema
)

export const keywordFilterOnlyAlertSchema = generateAlertSchema(
  apiKeywordFilterSchema
)

export const previewAlertSchema = z.union([
  naturalLanguageAlertSchema,
  patentAlertSchema,
  keywordFilterOnlyAlertSchema,
])

export type PreviewAlert = z.infer<typeof previewAlertSchema>
